import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CoachAnalysisRenderer from './CoachAnalysisRenderer';

const HistoryItem = ({ item, index, onRevert, canEdit, setConfirmRevertIdx }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative pl-6 border-l-2 border-indigo-100 last:border-0 pb-6">
      {/* Timeline dot */}
      <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white ${index === 0 ? 'bg-indigo-600' : 'bg-indigo-200'}`}></div>
      
      {/* Header of item */}
      <div 
        className="flex justify-between items-start mb-2 cursor-pointer group hover:bg-gray-50 rounded-lg -ml-2 p-2 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs text-gray-400 font-mono">
              {new Date(item.created_at).toLocaleString()}
            </span>
            <span className={`text-gray-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
          {item.request_text ? (
            <div className="font-bold text-gray-800 text-sm md:text-base italic group-hover:text-indigo-700 transition-colors">
              "{item.request_text}"
            </div>
          ) : (
            <div className="font-bold text-gray-500 text-sm md:text-base italic">
              {t('modals.modification_no_text') || 'Modification'}
            </div>
          )}
        </div>
        
        {/* Revert button for this specific item */}
        {index === 0 && onRevert && item.has_plan_json_before && canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmRevertIdx(index);
            }}
            className="md:hidden text-xs font-bold text-red-600 border border-red-200 bg-red-50 px-2 py-1 rounded ml-2 whitespace-nowrap z-10 relative hover:bg-red-100"
          >
            {t('modals.revert')}
          </button>
        )}
      </div>

      {/* Analysis Body */}
      {isOpen && (
        <div className="bg-gray-50/80 rounded-lg p-4 border border-gray-100 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="font-bold text-indigo-900 text-xs uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="text-lg">🤖</span> {t('modals.coach_analysis')}
          </div>
          
          <CoachAnalysisRenderer analysis={item.analysis_json} />
        </div>
      )}
    </div>
  );
};

export default function PlanHistoryModal({ isOpen, onClose, history, onRevert, canEdit }) {
  const { t } = useTranslation();
  const [confirmRevertIdx, setConfirmRevertIdx] = useState(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h3 className="text-xl font-bold text-gray-800">{t('modals.history_title')}</h3>
          <div className="flex items-center gap-2">
            {/* Global Revert (revert last) */}
            {onRevert && history && history.length > 0 && history[0].has_plan_json_before && canEdit && (
               <button
                 type="button"
                 onClick={() => setConfirmRevertIdx(0)}
                 className="hidden md:block px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 text-sm font-extrabold transition"
               >
                 {t('modals.revert_last')}
               </button>
            )}
            <button 
              onClick={onClose} 
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
            >
              ✕
            </button>
          </div>
        </div>

        {/* List */}
        <div className="p-6 overflow-y-auto min-h-0 space-y-2">
          {history && history.length > 0 ? (
            history.map((item, index) => (
              <HistoryItem 
                key={index} 
                item={item} 
                index={index} 
                onRevert={onRevert} 
                canEdit={canEdit}
                setConfirmRevertIdx={setConfirmRevertIdx}
              />
            ))
          ) : (
            <div className="text-center text-gray-500 py-10 italic">
              {t('modals.no_history')}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Overlay for Revert */}
      {confirmRevertIdx !== null && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4 z-[60] backdrop-blur-sm rounded-xl">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full border border-gray-200 animate-in fade-in zoom-in duration-200">
            <h4 className="text-lg font-bold text-gray-900 mb-2">{t('modals.revert_title')}</h4>
            <p className="text-sm text-gray-600 mb-6">{t('modals.revert_subtitle')}</p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setConfirmRevertIdx(null)}
                className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition"
              >
                {t('modals.cancel')}
              </button>
              <button 
                onClick={() => {
                  onRevert();
                  setConfirmRevertIdx(null);
                }}
                className="px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-sm transition"
              >
                {t('modals.confirm_revert')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
